/**
 * Lightweight SSE consumer for the conversation-first wizard.
 *
 * The `offline-session` worker's `POST /forms/generate` endpoint streams
 * `text/event-stream` events with a JSON `data:` payload per event. Browsers'
 * `EventSource` API cannot speak POST + JSON body, so we roll our own:
 * `fetch()` + `ReadableStream` + a 2-blank-line SSE event delimiter parser.
 *
 * Public surface intentionally tiny: one async generator. The wizard wraps
 * it in `for await`, dispatches each event to local state, and aborts via
 * the supplied `AbortSignal` on unmount.
 *
 * Heartbeat lines (`:heartbeat`) are silently dropped, they exist only to
 * keep Cloudflare from tearing down idle connections.
 *
 * @module lib/sse-consumer
 */

import type { FormField } from '../types';
import type { FormTemplateKey } from './form-templates';
import { offlineSessionWorkerUrl } from './worker-urls';
import { isFormField } from './form-capabilities';
import { isGeneratedGifQuery, type GeneratedGifQuery } from './generated-gif-intent';
import { parseFormImportReport, type FormImportReport } from './form-import-report';
import { conversationSectionSchema, type ConversationSection } from './conversation-sections';
import {
  GenerationRequestError,
  isFormGenerationErrorCode,
  isRetryableGenerationError,
  isRetryableHttpStatus,
  type FormGenerationErrorCode,
} from './generation-errors';

/** One entry in `SseProductResearch.offerings`. Matches `BrandOffering`. */
export interface SseOffering {
  name: string;
  description: string;
}

/**
 * One voice or tone trait. `example` is optional but strongly preferred:
 * concrete brand phrases give the LLM something to mimic instead of
 * paraphrasing the trait label back.
 */
export interface SseVoiceAttribute {
  trait: string;
  example?: string;
}

/** Wire-side style guide. Each field is optional so partial guides still
 *  thread through (the worker renders only the lines that are present). */
export interface SseStyleGuide {
  formality?: 'casual' | 'neutral' | 'formal';
  sentence_length?: 'short' | 'medium' | 'long';
  jargon_level?: 'plain' | 'industry' | 'technical';
  do_use?: string[];
  avoid?: string[];
}

/**
 * Subset of `ProductResearch` the worker accepts. Mirrors the wire shape
 * declared in `workers/offline-session/src/handlers/generate-form.ts`.
 *
 * The five `brandNameCanonical` / `offerings` / `voiceAttributes` /
 * `toneAttributes` / `styleGuide` slots come from the brand-research v2
 * pipeline. Each is optional on the wire; the worker skips a section when
 * its source is missing or empty.
 */
export interface SseProductResearch {
  brandNameCanonical?: string;
  /** Workspace policy available to the generator for linked acknowledgement checkboxes. */
  privacyPolicyUrl?: string;
  summary?: string;
  keyFeatures?: string[];
  targetAudience?: string;
  valueProposition?: string;
  brandVoiceTone?: string;
  offerings?: SseOffering[];
  voiceAttributes?: SseVoiceAttribute[];
  toneAttributes?: SseVoiceAttribute[];
  styleGuide?: SseStyleGuide;
  researched_at?: string;
}

/**
 * Wire shape for an inline file attachment forwarded to Gemini's
 * `inline_data` part. Just the mime type + base64 payload, no client-only
 * metadata leaks across the network.
 */
export interface SseInlineAttachment {
  mimeType: string;
  dataBase64: string;
}

/** Request body sent to `/forms/generate`. */
export interface GenerateFormInput {
  /** Earlier questions available to conditional single-question generation. */
  conditionSources?: import('./question-conditions').QuestionConditionSource[];
  /** Restrict generation to one answer without conversation metadata. */
  scope?: 'single_question';
  /** Existing authoring context, never questions to regenerate. */
  existingFields?: { label: string; type: FormField['type'] }[];
  /** Purpose of the conversation receiving the new question. */
  campaignGoal?: string;
  /** Explicit capability version, absent for the legacy generation surface. */
  multimodalVersion?: 1;
  /** Explicit opt-in protects older clients from unknown section metadata events. */
  sectionsVersion?: 1;
  /** Admin's conversation objective (one-liner). */
  objective: string;
  /** Template key chosen on the bubble selector. */
  template: FormTemplateKey;
  /** Curated template selected by a suggestion card; absent for free-text requests. */
  templateId?: string;
  /**
   * Optional brand-specific intent line associated with the picked
   * objective card (the card's `hint`). Sent only on the card-pick path:
   * free-form chat submissions omit it. The worker renders the hint
   * verbatim inside an `<objective_hint>` block so Gemini sees the card's
   * WHY alongside the short bubble label, keeping the generated goal
   * anchored to the conversation's actual purpose.
   */
  objectiveHint?: string;
  /**
   * Optional brand context derived from the active workspace. The wizard now
   * threads the canonical brand profile (name, offerings, voice/tone, style
   * guide) instead of a raw URL: prompts that anchored on the URL produced
   * weaker, less brand-specific questions.
   */
  productResearch?: SseProductResearch;
  /**
   * Optional file attachment from the chat composer. Forwarded to Gemini as
   * an `inlineData` part so the model sees the document/image alongside the
   * prompt. Encoded client-side to keep the worker dependency-free.
   */
  attachment?: SseInlineAttachment;
}

/**
 * Discriminated union of events the wizard observes. Matches the worker's
 * `data: {...}` payloads. Comment lines (`:heartbeat`) are filtered upstream
 * and never reach the consumer as `SseEvent`.
 *
 * `title` is emitted at most once, before any `field` event, when Gemini
 * authored a non-empty headline for this conversation. `goal` follows the title
 * (and precedes every `field`) when Gemini produced a non-empty,
 * formulated goal sentence, the wizard binds the conversation's saved Goal to
 * this event so the raw bubble label / typed prompt is never copied
 * verbatim into the goal slot.
 *
 * `error` carries only a public code (copy is resolved from it by
 * `lib/generation-errors`) and the retry decision; any wire `message` is
 * dropped. `serverReported` is true when the worker classified and captured
 * the failure, false when the client detected a malformed or unrecognized
 * event or a stream cut off mid-event.
 */
export type SseEvent =
  | { type: 'title'; title: string }
  | { type: 'goal'; goal: string }
  | { type: 'field'; field: FormField; gifQuery?: GeneratedGifQuery }
  | { type: 'sections'; sections: ConversationSection[] }
  | { type: 'import_report'; report: FormImportReport }
  | { type: 'done'; count: number }
  | SseErrorEvent;

/** A generation failure on the `/forms/generate` transport. */
export interface SseErrorEvent {
  type: 'error';
  /** Public code; unknown or missing codes become `failed`. */
  code: FormGenerationErrorCode;
  /**
   * The code table's answer, which a wire `retryable: false` can withhold
   * (a provider rejected the request) but a wire `true` can never grant.
   */
  retryable: boolean;
  /** True when the worker classified (and captured) the failure. */
  serverReported: boolean;
}

/**
 * Build an error event from a code and the worker's retry decision.
 *
 * @param code - Public code
 * @param serverReported - Whether the worker classified the failure
 * @param wireRetryable - The worker's `retryable` flag; only `false` counts
 * @returns The error event
 */
function errorEvent(code: FormGenerationErrorCode, serverReported: boolean, wireRetryable?: unknown): SseErrorEvent {
  return { type: 'error', code, retryable: isRetryableGenerationError(code) && wireRetryable !== false, serverReported };
}

/** Rejects malformed transport events before they reach the builder or draft. */
export function parseGeneratedSseEvent(value: unknown): SseEvent {
  // A malformed generated event is a rejected output, detected here.
  const invalid: SseEvent = errorEvent('unusable_output', false);
  if (!value || typeof value !== 'object') return invalid;
  const event = value as Record<string, unknown>;
  if (event.type === 'sections') {
    const sections = conversationSectionSchema.array().max(200).safeParse(event.sections);
    return sections.success ? { type: 'sections', sections: sections.data } : invalid;
  }
  if (event.type === 'import_report') {
    const report = parseFormImportReport(event.report);
    return report ? { type: 'import_report', report } : invalid;
  }
  if (event.type === 'field') {
    if (!isFormField(event.field, { allowIncompleteResources: true })) return invalid;
    if (event.gifQuery !== undefined && (!isGeneratedGifQuery(event.gifQuery) || event.field.type !== 'information'
      || !['text', 'image'].includes(event.field.contentVariant ?? '') || !(event.field.body?.trim() || event.field.title?.trim()))) return invalid;
    return { type: 'field', field: event.field, ...(isGeneratedGifQuery(event.gifQuery) ? { gifQuery: event.gifQuery } : {}) };
  }
  if (event.type === 'title' && typeof event.title === 'string') return { type: 'title', title: event.title };
  if (event.type === 'goal' && typeof event.goal === 'string') return { type: 'goal', goal: event.goal };
  if (event.type === 'error') {
    // Only the code crosses into app state; copy never comes from the wire.
    return isFormGenerationErrorCode(event.code) ? errorEvent(event.code, true, event.retryable) : errorEvent('failed', false);
  }
  if (event.type === 'done' && typeof event.count === 'number' && Number.isInteger(event.count) && event.count >= 0) return { type: 'done', count: event.count };
  return invalid;
}

/**
 * Required auth plus optional injection points so tests can stub `fetch`
 * and the worker URL without touching `import.meta.env`.
 */
export interface StreamFormGenerationOptions {
  /** Supabase access token; the worker validates it as a Bearer JWT (audit S3). */
  accessToken: string;
  /** Override for the worker base URL (defaults to `VITE_OFFLINE_SESSION_WORKER_URL`). */
  workerUrl?: string;
  /** Override for `fetch`. Defaults to the global. */
  fetchImpl?: typeof fetch;
}

/**
 * Parse a single decoded SSE chunk and yield any complete events. Returns
 * the unconsumed leftover so the caller can prepend it to the next chunk.
 *
 * SSE event boundary is two consecutive line endings (`\n\n` or `\r\n\r\n`).
 * Anything before the last `\n\n` is fully-formed and parseable; the rest is
 * a partial event still being accumulated.
 *
 * Heartbeat comment lines (lines starting with `:`) are silently dropped
 * the spec says comments must be ignored by clients, but we pre-filter them
 * so callers don't have to.
 *
 * @param buffer - Complete events, each ending in a blank line
 * @param unparseable - Code for a payload that is not JSON: a complete frame
 *   is a rejected output; the trailing partial frame of a closed stream is a
 *   cut-off transport, so the caller passes `failed` for it
 */
function* parseEvents(
  buffer: string,
  unparseable: FormGenerationErrorCode = 'unusable_output',
): Generator<SseEvent, void, void> {
  const blocks = buffer.split('\n\n');
  // Last block is intentionally left for the caller to re-buffer.
  for (let i = 0; i < blocks.length - 1; i++) {
    const block = blocks[i];
    if (!block) continue;

    // Find the first `data:` line (per SSE spec, multiple data: lines
    // concatenate, but our worker emits exactly one per event).
    const dataLine = block
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line.startsWith('data:'));
    if (!dataLine) continue; // pure comment block (e.g. `:heartbeat`)

    const payload = dataLine.slice('data:'.length).trim();
    if (!payload) continue;

    let parsed: unknown;
    try {
      parsed = JSON.parse(payload);
    } catch {
      yield errorEvent(unparseable, false);
      continue;
    }
    yield parseGeneratedSseEvent(parsed);
  }
}

/**
 * Turn a non-OK `/forms/generate` response into a coded failure. Reads only
 * `error_code` and `retryable` from a JSON body, never its text. Without a
 * recognized code the failure is `failed`, client-detected, and retryable
 * only for 408, 429 and 5xx: any other 4xx rejects the request itself (for
 * example an invalid privacy URL), so another attempt fails the same way.
 *
 * @param response - The non-OK response
 * @returns The error to throw
 */
async function requestFailure(response: Response): Promise<GenerationRequestError> {
  type ProblemBody = { error_code?: unknown; retryable?: unknown } | null;
  // Not JSON (a gateway page): only the status is known.
  const body = (await response.json().catch(() => null)) as ProblemBody;
  const wireRetryable = typeof body?.retryable === 'boolean' ? body.retryable : isRetryableHttpStatus(response.status);
  if (body && isFormGenerationErrorCode(body.error_code)) {
    return new GenerationRequestError(body.error_code, { retryable: wireRetryable, serverReported: true });
  }
  return new GenerationRequestError('failed', { retryable: isRetryableHttpStatus(response.status), serverReported: false });
}

/**
 * Stream `FormField`s from `/forms/generate` as an async generator of
 * `SseEvent`s. The wizard consumes via `for await` and reacts per event.
 *
 * @param input    Body sent to the worker.
 * @param signal   Aborts the underlying `fetch` and ends the generator.
 * @param options  Auth token plus optional `fetch` / worker-URL overrides.
 * @yields         Each `field`, `done`, or `error` payload received.
 * @throws         {@link GenerationRequestError} with the public code and a
 *                 truthful retry decision when the response is not OK.
 */
export async function* streamFormGeneration(
  input: GenerateFormInput,
  signal: AbortSignal,
  options: StreamFormGenerationOptions,
): AsyncGenerator<SseEvent, void, void> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const baseUrl = options.workerUrl ?? offlineSessionWorkerUrl();

  const response = await fetchImpl(`${baseUrl}/forms/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${options.accessToken}`,
    },
    body: JSON.stringify(input),
    signal,
  });

  if (!response.ok) throw await requestFailure(response);
  if (!response.body) throw new GenerationRequestError('failed', { serverReported: false });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Yield every fully-formed event in the buffer; keep the trailing
      // partial event (after the last `\n\n`) for the next iteration.
      const lastBoundary = buffer.lastIndexOf('\n\n');
      if (lastBoundary === -1) continue;

      const consumable = buffer.slice(0, lastBoundary + 2);
      buffer = buffer.slice(lastBoundary + 2);

      for (const event of parseEvents(consumable)) {
        yield event;
      }
    }

    // Flush any final event that landed without a trailing newline pair. A
    // payload that does not parse here was cut off when the stream closed.
    if (buffer.trim().length > 0) {
      for (const event of parseEvents(buffer + '\n\n', 'failed')) {
        yield event;
      }
    }
  } finally {
    reader.releaseLock();
  }
}
